/**
 * Tutorial progress, persisted per browser (it's a per-device teaching aid, not
 * an account achievement — a guest who learns the controls shouldn't lose that
 * by not signing in).
 *
 * Stored as a comma-separated list of completed mission ids so the format stays
 * readable and survives the curriculum changing: ids that no longer exist are
 * dropped on read, and new missions simply show up as not-yet-done.
 */
import { MISSIONS, type Mission } from './missions';
import { missionById } from './runtime';

const KEY = 'carom.tutorial';

/** Read the set of completed mission ids, ignoring any that no longer exist. */
export function loadProgress(): Set<string> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return new Set(); // private mode / storage disabled — just don't remember
  }
  const ids = (raw ?? '').split(',').filter((id) => id && missionById(id));
  return new Set(ids);
}

export function saveProgress(done: ReadonlySet<string>): void {
  try {
    // Persist in curriculum order so the stored value is stable and diffable.
    localStorage.setItem(KEY, MISSIONS.filter((m) => done.has(m.id)).map((m) => m.id).join(','));
  } catch {
    /* storage disabled — progress is just not remembered */
  }
}

export function clearProgress(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The first mission not yet completed, or null when the course is finished.
 *
 * `list` is the curriculum for the device in play (see `missionsFor`) — on touch
 * it is a subset, so "finished" has to mean "finished everything this device can
 * actually be taught", not "finished every mission that exists".
 */
export function nextMission(done: ReadonlySet<string>, list: readonly Mission[] = MISSIONS): Mission | null {
  return list.find((m) => !done.has(m.id)) ?? null;
}

/** Has the player finished (or at least started) the course? Drives the menu label. */
export function tutorialStatus(
  done: ReadonlySet<string>,
  list: readonly Mission[] = MISSIONS
): 'new' | 'partial' | 'complete' {
  const cleared = list.filter((m) => done.has(m.id)).length;
  if (cleared === 0) return 'new';
  return cleared >= list.length ? 'complete' : 'partial';
}

/** How many of this device's missions are cleared (the menu shows the step number). */
export function clearedCount(done: ReadonlySet<string>, list: readonly Mission[] = MISSIONS): number {
  return list.filter((m) => done.has(m.id)).length;
}

/**
 * Where to go after finishing (or skipping) `current`: the next incomplete
 * mission further down the curriculum, or — once the end is reached — the first
 * incomplete one from the top, so anything skipped comes back around instead of
 * being silently lost. Null when everything is done.
 */
export function nextMissionAfter(
  current: Mission,
  done: ReadonlySet<string>,
  list: readonly Mission[] = MISSIONS
): Mission | null {
  const i = list.indexOf(current);
  const ahead = list.slice(i + 1).find((m) => !done.has(m.id));
  return ahead ?? nextMission(done, list);
}
